import { Avatar, Box, Button, Divider, Drawer, Fade, Grid, LinearProgress, Link, List, ListItem, ListItemIcon, ListItemText, makeStyles, Theme } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import IconButton from '@material-ui/core/IconButton';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { ExitToApp, Headset, Refresh, Settings, Warning } from '@material-ui/icons';
import DarkIcon from '@material-ui/icons/Brightness4';
import LightIcon from '@material-ui/icons/Brightness7';
import MenuIcon from '@material-ui/icons/Menu';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import LogoImage from '../../assets/images/logo..png';
import ListItemCollapse from '../../components/ListItemCollapse';
import { useAuth } from '../../hooks/auth';
import { usePermissions } from '../../hooks/permissions';
import { useTheme } from '../../hooks/theme';
import { Helpers } from '../../utils/Helpers';
import PageContent from './Page.Content';
import PageSection from './Page.Section';
import PageTitle from './Page.Title';
import PageToolbar from './Page.Toolbar';

interface Props {
    error?: string | boolean;
    loading?: boolean;
    children?: React.ReactNode;
}

function Page(props: Props) {

    const classes = useStyles();
    const permissions = usePermissions();
    const history = useHistory();
    const { pathname } = useLocation();

    const { enqueueSnackbar: alert } = useSnackbar();

    const { logout, data } = useAuth();
    const { isDarkMode, toggleTheme } = useTheme();

    const [drawer, setDrawer] = React.useState<boolean>(false);

    const toggleDrawer = () => setDrawer(!drawer);

    async function handleLogout() {
        try {
            await logout();
        } catch (error) {
            Helpers.error(error, alert);
        }
    }

    return (
        <Scrollbars
            autoHide={true}
            className={classes.scrollbar}
            hideTracksWhenNotNeeded={true}
        >
            <AppBar className={classes.menu} position="static">
                <Toolbar>
                    <IconButton edge="start" className={classes.menuButton} color="inherit" onClick={() => setDrawer(true)}>
                        <MenuIcon />
                    </IconButton>
                    <Link href="/propostas" color="inherit" underline="none">
                        <img src={LogoImage} alt="Logo" className={classes.logo} />
                    </Link>
                    <Box flexGrow={1}></Box>
                    <Box className={classes.options}>
                        <Link href="https://ksbbrasil.sysaidit.com/servicePortal/submitSR?category_id=428" target="_blank" color="inherit">
                            <IconButton color="inherit">
                                <Headset style={{ marginTop: '2px' }} />
                            </IconButton>
                        </Link>
                        <IconButton color="inherit" onClick={() => toggleTheme()}>
                            {isDarkMode ? <LightIcon /> : <DarkIcon />}
                        </IconButton>
                        {permissions.has('config.show') &&
                            <IconButton color="inherit" onClick={() => history.push('/configuracoes')}>
                                <Settings />
                            </IconButton>
                        }
                        {data.token &&
                            <IconButton color="inherit" onClick={() => handleLogout()}>
                                <ExitToApp />
                            </IconButton>
                        }
                    </Box>
                </Toolbar>
                <Fade in={props.loading} timeout={500}>
                    <LinearProgress color="secondary" />
                </Fade>
            </AppBar>
            <Drawer anchor="left" open={drawer} onClose={toggleDrawer}>
                <Box width={300}>
                    <Scrollbars
                        autoHide={true}
                        className={classes.scrollbar}
                        hideTracksWhenNotNeeded={true}
                    >
                        <List>
                            <Box pl={2} pt={2}>
                                <Avatar className={classes.largeAvatar}>
                                    {data.user.nome.charAt(0).toUpperCase()}
                                </Avatar>
                            </Box>
                            <ListItem>
                                <ListItemText
                                    primary={data.user.nome}
                                    secondary={data.user.email}
                                />
                            </ListItem>
                        </List>
                        <Divider></Divider>
                        <List>
                            <ListItemCollapse title="Acessórios" style={permissions.menu(['acessorio.show', 'acessorio.index'])}>
                                <ListItem component="a" button style={permissions.style('acessorio.show')} selected={pathname === '/acessorios/create'} href="/acessorios/create">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Novo Acessório" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('acessorio.index')} selected={pathname === '/acessorios'} href="/acessorios">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Acessórios" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Bombas" style={permissions.menu(['bomba.show', 'bomba.index'])}>
                                <ListItem component="a" button style={permissions.style('bomba.show')} selected={pathname === '/bombas/create'} href="/bombas/create">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Nova Bomba" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('bomba.index')} selected={pathname === '/bombas'} href="/bombas">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Bombas" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Clientes" style={permissions.menu('cliente.index')}>
                                <ListItem component="a" button style={permissions.style('cliente.index')} selected={pathname === '/clientes'} href="/clientes">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Clientes" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Descontos" style={permissions.menu(['desconto.index', 'descontoinf.index'])}>
                                <ListItem component="a" button style={permissions.style('desconto.index')} selected={pathname === '/descontos/cliente'} href="/descontos/cliente">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Desconto de Cliente" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('descontoinf.index')} selected={pathname === '/descontos/info'} href="/descontos/info">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Desconto Info Comercial" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Grupos" style={permissions.menu(['grupo.show', 'grupo.index'])}>
                                <ListItem component="a" button style={permissions.style('grupo.show')} selected={pathname === '/grupos/create'} href="/grupos/create">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Novo Atrelamento" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('grupo.index')} selected={pathname === '/grupos'} href="/grupos">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Atrelamentos" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Modelos" style={permissions.menu(['modelo.show', 'modelo.index'])}>
                                <ListItem component="a" button style={permissions.style('modelo.show')} selected={pathname === '/modelos/create'} href="/modelos/create">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Novo Modelo" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('modelo.index')} selected={pathname === '/modelos'} href="/modelos">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Modelos" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Preços" style={permissions.menu(['tabela.show', 'tabela.index', 'cpp.show', 'cpp.index', 'especial.index'])}>
                                <ListItem component="a" button style={permissions.style('tabela.show')} selected={pathname === '/tabelas/create'} href="/tabelas/create">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Nova Tabela" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('tabela.index')} selected={pathname === '/tabelas'} href="/tabelas">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consultar Tabelas" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('cpp.show')} selected={pathname === '/cpp/create'} href="/cpp/create">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Novo CPP" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('cpp.index')} selected={pathname === '/cpp'} href="/cpp">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consultar CPP" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('especial.index')} selected={pathname === '/precos/variantes'} href="/precos/variantes">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Acessórios Variantes" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Propostas" style={permissions.menu(['proposta.create', 'proposta.index'])}>
                                <ListItem component="a" button style={permissions.style('proposta.create')} selected={pathname === '/propostas/create'} href="/propostas/create">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Nova Proposta" />
                                </ListItem>
                                <ListItem component="a" button style={permissions.style('proposta.index')} selected={pathname === '/propostas'} href="/propostas">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Propostas" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Segmentos" style={permissions.menu('segmento.index')}>
                                <ListItem component="a" button style={permissions.style('segmento.index')} selected={pathname === '/segmentos'} href="/segmentos">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Segmentos" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Subtipos" style={permissions.menu('hierarquia.index')}>
                                <ListItem component="a" button style={permissions.style('hierarquia.index')} selected={pathname === '/subtipos'} href="/subtipos">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Subtipos" />
                                </ListItem>
                            </ListItemCollapse>
                            <ListItemCollapse title="Usuários" style={permissions.menu('user.index')}>
                                <ListItem component="a" button style={permissions.style('user.index')} selected={pathname === '/usuarios'} href="/usuarios">
                                    <ListItemIcon></ListItemIcon>
                                    <ListItemText primary="Consulta de Usuários" />
                                </ListItem>
                            </ListItemCollapse>
                        </List>
                    </Scrollbars>
                </Box>
            </Drawer>
            {props.error ?
                <Fade in={!props.loading} timeout={500}>
                    <Grid
                        container
                        className={classes.error}
                        direction="column"
                        alignItems="center"
                        spacing={2}
                    >
                        <Grid item>
                            <Warning className={classes.warning} color="error" />
                        </Grid>
                        <Grid item>
                            <Typography variant="h6" align="center">
                                Oops, ocorreu um erro!
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body1" align="center">
                                {typeof props.error === 'string'
                                    ? props.error
                                    : 'Não foi possível realizar a conexão, tente atualizar a página.'
                                }
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                color="primary"
                                variant="contained"
                                startIcon={<Refresh />}
                                onClick={() => window.location.reload()}>
                                Tentar Novamente
                            </Button>
                        </Grid>
                    </Grid>
                </Fade>
                :
                <Fade in={!props.loading} timeout={500}>
                    <Grid container justifyContent="center">
                        <Grid item xs={10}>
                            {props.children}
                        </Grid>
                    </Grid>
                </Fade>
            }
        </Scrollbars>
    );
}

Page.Section = PageSection;
Page.Toolbar = PageToolbar;
Page.Title = PageTitle;
Page.Content = PageContent;

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        flexGrow: 1,
    },
    error: {
        flex: 1,
        padding: theme.spacing(4),
        justifyContent: 'center',
        alignItems: 'center',
    },
    warning: {
        fontSize: theme.typography.pxToRem(64),
    },
    menu: {
        marginBottom: theme.spacing(4),
    },
    menuButton: {
        marginRight: theme.spacing(2),
    },
    largeAvatar: {
        backgroundColor: theme.palette.primary.main,
        width: theme.spacing(7),
        height: theme.spacing(7),
        fontSize: theme.spacing(4),
    },
    scrollbar: {
        height: '100vh !important',
    },
    options: {
        '& > *': {
            margin: '1px',
        },
    },
    logo: {
        height: 20,
        marginTop: 5
    }
}));

export default Page;